import React from 'react';
import { TableConfig } from '../../../../../v2BaseComponents/RichTable/types';
import { APITypes } from '../../../../../../primary-record-types';
import { getIntl } from '../../../../../../utils/intl';
import { useRichTableState } from '../../../../../v2BaseComponents/RichTable/useRichTableState';
import RichTable from '../../../../../v2BaseComponents/RichTable';
import {
    useChainedSideDrawer,
    useMaybeRenderedSideDrawerToggle,
} from '../../../../../v2BaseComponents/SideDrawer/MaybeRenderedSideDrawer';
import { useResourcesTabNavigation } from '../../../../../../utils/appNavigationNames';
import { useOrganizationId } from '../../../../../../hooks/api/useOrganization';
import useNotes from '../../../../../../hooks/api/useNotes';
import { multiLineTruncate } from '../../../../../../utils/multiLineTruncate';
import getDateString from '../../../../../../utils/getDateString';
import AddNoteSideDrawerContent from './AddNoteSideDrawerContent';
import NoteDetailsSideDrawerContent from './NoteDetailsSideDrawerContent';
import NoteEditSideDrawerContent from './NoteEditSideDrawerContent';
import { ContentStack } from '../../../../../v2BaseComponents/Stack';
import InfoIcon from '../../../../../v2BaseComponents/InfoIcon';
import { usePromptButton } from '../../../../../shared/PromptButton';
import { PromptActions } from '../../../../../v2BaseComponents/Prompt';

const intl = getIntl('resources-root-screen-notes-tab-table', {
    addNote: 'Add Note',
    editNote: 'Edit Note',
    noteDetails: 'Note Details',
    deleteNote: 'Delete Note',
    deleteNotes: 'Delete Notes',
    title: 'Title',
    content: 'Content',
    createdAt: 'Created',
    updatedAt: 'Updated',
    deleteNoteHeader: 'Delete Note?',
    deleteNoteContent: 'Are you sure you want to delete the note "{name}"?  This action cannot be undone.',
    deleteNoteConfirm: 'Delete Note',
    deleteNotesHeader: 'Delete Notes?',
    deleteNotesContent: 'Are you sure you want to delete {count} notes?  This action cannot be undone.',
    deleteNotesConfirm: 'Delete Notes',
    delete: 'Delete',
    cancel: 'Cancel',
});

export type NotesTableOwnProps = {};

export type NotesTableProps = NotesTableOwnProps;

type TableItem = APITypes.notesService.NoteListItem;
type TimestampType = 'create' | 'update';
const getRawTimestamp = (item: TableItem, type: TimestampType) => (type === 'create' ? item.createdAt : item.updatedAt);

const NotesTable: React.FC<NotesTableProps> = (_props) => {
    const resourcesTabNavigator = useResourcesTabNavigation();
    const [tableState, setTableState] = useRichTableState();
    const organizationId = useOrganizationId();
    const { notes, deleteNote, deleteNotes, isFetchingNotes } = useNotes({
        noteQueryArgs: {
            entityType: 'organization',
            organizationId,
        },
    });
    const getTitle = (item: TableItem) => item.title;
    const getContentDisplay = (item: TableItem) => multiLineTruncate(item.content, 50);
    const getTimeStampDisplay = (type: TimestampType) => (item: TableItem) =>
        getDateString(getRawTimestamp(item, type), 'date-time');
    const getTimeStampSortValue = (type: TimestampType) => (item: TableItem) => new Date(getRawTimestamp(item, type));

    const addNoteSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'addOrganizationNote',
        title: intl('addNote'),
        renderContent: (onClose) => <AddNoteSideDrawerContent onClose={onClose} />,
        mobileOnFocus: () => resourcesTabNavigator.navigate('add-note'),
    });
    const noteEditSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'editOrganizationNote',
        title: intl('editNote'),
        renderContent: (onClose) => (
            <NoteEditSideDrawerContent onClose={onClose} noteId={tableState.selectedRowKeys[0]} />
        ),
        mobileOnFocus: () => resourcesTabNavigator.navigate('edit-note', { noteId: tableState.selectedRowKeys[0] }),
    });
    const toggleEditFromDetails = useChainedSideDrawer(noteEditSideDrawer.onOpen);
    const noteDetailsSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'noteDetails',
        title: intl('noteDetails'),
        renderContent: (onClose) => (
            <NoteDetailsSideDrawerContent
                onClose={onClose}
                onEditNote={() => {
                    toggleEditFromDetails();
                    onClose(true);
                }}
                noteId={tableState.selectedRowKeys[0]}
            />
        ),
        mobileOnFocus: () => resourcesTabNavigator.navigate('note', { noteId: tableState.selectedRowKeys[0] }),
    });
    const selectedNotes = tableState.selectedRowKeys.map((key) => notes.notes.find((note) => note.noteId === key));

    const deleteButtonModal = usePromptButton({
        renderPromptContents: (onClose) => {
            const isMultiple = tableState.selectedRowKeys.length > 1;
            return (
                <ContentStack>
                    <InfoIcon
                        alertIconProps={{
                            name: 'AlertTriangle',
                            variant: 'error',
                        }}
                        title={intl(isMultiple ? 'deleteNotesHeader' : 'deleteNoteHeader')}
                        subText={
                            isMultiple
                                ? intl('deleteNoteContent', {
                                      count: tableState.selectedRowKeys.length,
                                  })
                                : intl('deleteNoteContent', { name: selectedNotes[0]?.title || '' })
                        }
                    />
                    <PromptActions
                        actions={[
                            {
                                title: intl(isMultiple ? 'deleteNotesConfirm' : 'deleteNoteConfirm'),
                                onPress: () => {
                                    if (isMultiple) {
                                        deleteNotes(tableState.selectedRowKeys);
                                    } else {
                                        deleteNote(tableState.selectedRowKeys[0]);
                                    }
                                    onClose();
                                },
                                variant: 'alert',
                                icon: 'Trash2',
                                testID: 'delete-note-confirm',
                            },
                            {
                                title: intl('cancel'),
                                onPress: onClose,
                                variant: 'secondary',
                                icon: 'X',
                                testID: 'delete-note-cancel',
                            },
                        ]}
                    />
                </ContentStack>
            );
        },
        promptTitle: intl('deleteNoteHeader'),
    });

    const notesTableConfig: TableConfig<TableItem> = {
        columns: [
            {
                label: intl('title'),
                columnKey: 'title',
                getSortValue: getTitle,
                getContentKey: getTitle,
                getSearchValue: getTitle,
                render: getTitle,
            },
            {
                label: intl('content'),
                columnKey: 'content',
                getSortValue: getContentDisplay,
                getContentKey: getContentDisplay,
                getSearchValue: (item) => item.plainTextContent,
                render: getContentDisplay,
                hideOnMobile: true,
            },
            {
                label: intl('createdAt'),
                columnKey: 'createdAt',
                getSortValue: getTimeStampSortValue('create'),
                getContentKey: getTimeStampDisplay('create'),
                getSearchValue: getTimeStampDisplay('create'),
                render: getTimeStampDisplay('create'),
                hideOnMobile: true,
            },
            {
                label: intl('updatedAt'),
                columnKey: 'updatedAt',
                getSortValue: getTimeStampSortValue('update'),
                getContentKey: getTimeStampDisplay('update'),
                getSearchValue: getTimeStampDisplay('update'),
                render: getTimeStampDisplay('update'),
                hideOnMobile: true,
            },
        ],
        getRowKey: (note) => note.noteId,
    };

    return (
        <>
            <RichTable
                config={notesTableConfig}
                state={tableState}
                onStateChange={setTableState}
                data={notes.notes}
                minWidth={'100%'}
                allowRowSelection
                style={{ height: '100%' }}
                isFetching={isFetchingNotes}
                actions={[
                    {
                        icon: 'Plus',
                        title: intl('addNote'),
                        onPress: addNoteSideDrawer.onOpen,
                        shouldShow: (selected) => selected.length === 0,
                        testID: 'add-org-note-button',
                    },
                    {
                        icon: 'Info',
                        title: intl('noteDetails'),
                        onPress: noteDetailsSideDrawer.onOpen,
                        shouldShow: (selected) => selected.length === 1,
                        testID: 'note-details-button',
                    },
                    {
                        icon: 'Edit',
                        title: intl('editNote'),
                        onPress: noteEditSideDrawer.onOpen,
                        shouldShow: (selected) => selected.length === 1,
                        testID: 'edit-org-note-button',
                    },
                    {
                        icon: 'Trash',
                        title: intl(tableState.selectedRowKeys.length === 1 ? 'deleteNote' : 'deleteNotes'),
                        onPress: deleteButtonModal.onPress,
                        shouldShow: (selected) => selected.length >= 1,
                        testID: 'delete-org-note-button',
                    },
                ]}
            />
            {addNoteSideDrawer.SideDrawer}
            {noteDetailsSideDrawer.SideDrawer}
            {noteEditSideDrawer.SideDrawer}
            {deleteButtonModal.Prompt}
        </>
    );
};

export default React.memo(NotesTable);
